import React, { useEffect, useRef, useState } from "react";
import TeacherPicker from "./TeacherPicker";
import "./ClassroomSetupForm.css";

const MAX_CLASS_NAME_LENGTH = 100;

export const getInitialClassName = (entry) => {
  if (entry?.setupStatus === "setup_required") return "";
  return entry?.draft?.className || entry?.currentClass?.name || "";
};

function ClassroomSetupForm({
  entry,
  status,
  onSearchTeachers,
  onInviteTeacher,
  onContinue,
  onComplete,
  onBack,
  onClose,
  onDirtyChange,
}) {
  const initialClassName = getInitialClassName(entry);
  const initialTeacher = entry.teacherSelection || null;
  const [className, setClassName] = useState(initialClassName);
  const [teacher, setTeacher] = useState(initialTeacher);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const classNameRef = useRef(null);
  const teacherHeadingRef = useRef(null);
  const isSaving = status === "saving";
  const dirty = className !== initialClassName || teacher?.id !== initialTeacher?.id;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const validate = () => {
    const nextErrors = {};
    const normalizedName = className.trim();
    if (!normalizedName) nextErrors.className = "Enter this year’s class name.";
    else if (normalizedName.length > MAX_CLASS_NAME_LENGTH) {
      nextErrors.className = `Use ${MAX_CLASS_NAME_LENGTH} characters or fewer.`;
    }
    if (!teacher) nextErrors.teacher = "Select an active or pending teacher.";
    setErrors(nextErrors);
    if (nextErrors.className) classNameRef.current?.focus();
    else if (nextErrors.teacher) teacherHeadingRef.current?.focus();
    return Object.keys(nextErrors).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    if (!validate()) return;
    try {
      const saved = await onContinue({ className, teacher });
      onDirtyChange?.(false);
      onComplete(saved);
    } catch (error) {
      const serverErrors = error?.response?.data?.errors;
      if (serverErrors?.className) {
        setErrors((current) => ({ ...current, className: serverErrors.className }));
        classNameRef.current?.focus();
      } else if (error?.response?.status !== 409) {
        setSubmitError("We couldn’t save the setup. Your entries are still here; try again.");
      }
    }
  };

  return (
    <main className="handover-class-setup" aria-labelledby="handover-class-setup-title">
      <div className="handover-setup-top-actions">
        <button type="button" className="handover-back-link" onClick={onBack}>← Back to My Classes</button>
        <button type="button" className="handover-close-setup" aria-label="Close setup" onClick={onClose}>×</button>
      </div>

      <header className="handover-class-setup-header">
        <div>
          <p className="handover-section-eyebrow">Classroom setup</p>
          <h1 id="handover-class-setup-title">Set up this class</h1>
          <p>You’re setting up a converted class.</p>
          <p>We just need a few details to get started.</p>
        </div>
        <aside className="handover-history-cue" aria-label="Previous classroom details">
          <img className="handover-history-icon" src="/assets/imgs/Asset-class.webp" alt="" />
          <div>
            <strong>Converted from last year’s {entry.previousClass?.gradeLabel || "class"} classes</strong>
            <span>Previously {entry.previousClass?.teacherName ? `${entry.previousClass.teacherName}’s ` : ""}{entry.previousClass?.name}</span>
          </div>
        </aside>
      </header>

      <form onSubmit={submit} noValidate>
        <section className="handover-setup-step" aria-labelledby="class-name-heading">
          <div className="handover-step-heading">
            <span aria-hidden="true">1</span>
            <h2 id="class-name-heading">What is this class called this year?</h2>
          </div>
          <label htmlFor="handover-class-name">Enter the name exactly as it appears in your school MIS, e.g. Aladdin, iSAMS.</label>
          <input
            ref={classNameRef}
            id="handover-class-name"
            type="text"
            placeholder="Enter class name"
            value={className}
            onChange={(event) => {
              setClassName(event.target.value);
              setErrors((current) => ({ ...current, className: "" }));
            }}
            maxLength={MAX_CLASS_NAME_LENGTH + 1}
            aria-invalid={Boolean(errors.className)}
            aria-describedby={errors.className ? "handover-class-name-error" : "handover-class-name-hint"}
            disabled={isSaving}
          />
          <p id="handover-class-name-hint" className="handover-field-hint">The year group and academic year stay unchanged.</p>
          {errors.className && <p id="handover-class-name-error" className="handover-field-error" role="alert">{errors.className}</p>}
        </section>

        <section className="handover-setup-step handover-setup-step--teacher" aria-labelledby="teacher-heading">
          <div className="handover-step-heading">
            <span aria-hidden="true">2</span>
            <h2 id="teacher-heading" ref={teacherHeadingRef} tabIndex="-1">Who is the classroom teacher?</h2>
          </div>
          <TeacherPicker
            selected={teacher}
            onSelect={(nextTeacher) => {
              setTeacher(nextTeacher);
              setErrors((current) => ({ ...current, teacher: "" }));
            }}
            onSearch={onSearchTeachers}
            onInvite={onInviteTeacher}
            disabled={isSaving}
          />
          {errors.teacher && <p className="handover-field-error" role="alert">{errors.teacher}</p>}
        </section>

        {submitError && <p className="handover-submit-error" role="alert">{submitError}</p>}
        <footer className="handover-setup-footer">
          <button type="button" className="handover-secondary-action" onClick={onBack} disabled={isSaving}>← Back</button>
          <button type="submit" className="handover-primary-action" disabled={isSaving}>
            {isSaving ? "Saving…" : "Continue →"}
          </button>
        </footer>
      </form>
    </main>
  );
}

export default ClassroomSetupForm;
